import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Badge,
  Box,
  Button,
  Chip,
  Collapse,
  Grid,
  IconButton,
  MenuItem,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  Plus,
  RotateCw,
  Download,
  Settings2,
  BarChart3,
  SlidersHorizontal,
  X,
  Search,
  Inbox,
  Ticket,
  Clock3,
  AlertTriangle,
  CheckCircle2,
  Star,
  MessageSquareWarning,
} from "lucide-react";
import * as XLSX from "xlsx";

import ComplaintForm from "./ComplaintForm";
import {
  EmptyState,
  GlassCard,
  ListSkeleton,
  PageHeader,
  PageShell,
  PersonAvatar,
  PriorityChip,
  PERMISSION,
  SLA_COLORS,
  SlaChip,
  StatRow,
  StatTile,
  StatusChip,
  T,
  TileSkeleton,
  TYPE,
  can,
  formatCompact,
  formatCurrency,
  formatDateTime,
  formatDuration,
  formatRelative,
  labelSx,
  ghostButtonSx,
  primaryButtonSx,
} from "./shared";
import { fetchComplaints, fetchFormMeta, fetchReport, errorMessage } from "./api";

const EMPTY_FILTERS = {
  search: "",
  status: "",
  priority: "",
  category: "",
  source: "",
  slaState: "",
  assignedTo: "",
  from: "",
  to: "",
};

// The views an agent actually switches between all day. Each is just a preset
// over the same filter object, so the grid, export and paging need no special
// cases.
const QUICK_VIEWS = [
  { key: "all", label: "All", filters: {} },
  { key: "open", label: "Open", filters: { status: "New,In Progress,Escalated,Reopened" } },
  { key: "overdue", label: "Overdue", filters: { slaState: "overdue" } },
  { key: "at_risk", label: "At risk", filters: { slaState: "at_risk" } },
  { key: "escalated", label: "Escalated", filters: { status: "Escalated" } },
  { key: "unassigned", label: "Unassigned", filters: { assignedTo: "unassigned" } },
  { key: "resolved", label: "Resolved", filters: { status: "Resolved,Closed" } },
];

/**
 * A grid cell with a primary line and a muted secondary line.
 *
 * Every stacked cell goes through this so they all share one line-height and
 * one block height — mixing ad-hoc Box/Typography stacks per column is what let
 * the columns drift out of alignment with each other.
 */
const TwoLine = ({ primary, secondary, primarySx, fallback = "—" }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography
      noWrap
      sx={{ fontSize: TYPE.body, color: T.ink, lineHeight: 1.4, ...primarySx }}
    >
      {primary || fallback}
    </Typography>
    {secondary ? (
      <Typography noWrap sx={{ fontSize: TYPE.micro, color: T.inkMuted, lineHeight: 1.4 }}>
        {secondary}
      </Typography>
    ) : null}
  </Box>
);

const Complaints = () => {
  const navigate = useNavigate();

  const [meta, setMeta] = useState(null);
  const [kpis, setKpis] = useState(null);
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ total: 0 });
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [sortModel, setSortModel] = useState([{ field: "createdAt", sort: "desc" }]);

  const [view, setView] = useState("all");
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  // Applied separately from `filters` so typing in the search box does not fire
  // a request per keystroke.
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS);
  const [showFilters, setShowFilters] = useState(false);

  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [toast, setToast] = useState({ open: false, message: "", severity: "success" });
  const [loadError, setLoadError] = useState("");

  // Memoised: a fresh array each render would change loadKpis' identity and
  // re-fire its effect on every render.
  const permissions = useMemo(() => meta?.me?.permissions || [], [meta]);

  const notify = (message, severity = "success") => setToast({ open: true, message, severity });

  // Debounce the search box; other filters apply immediately.
  useEffect(() => {
    const isSearchEdit = filters.search !== appliedFilters.search;
    const timer = setTimeout(() => {
      setAppliedFilters(filters);
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    }, isSearchEdit ? 400 : 0);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    let active = true;

    fetchFormMeta()
      .then((data) => {
        if (active) setMeta(data);
      })
      .catch((err) => {
        if (active) setLoadError(errorMessage(err, "Could not load the complaint module"));
      });

    return () => {
      active = false;
    };
  }, []);

  const loadComplaints = useCallback(async () => {
    setLoading(true);
    try {
      const sort = sortModel[0] || { field: "createdAt", sort: "desc" };
      const params = {
        page: paginationModel.page + 1,
        limit: paginationModel.pageSize,
        sortBy: sort.field,
        sortOrder: sort.sort,
      };

      Object.entries(appliedFilters).forEach(([key, value]) => {
        if (value) params[key] = value;
      });

      const result = await fetchComplaints(params);
      setRows(result.rows);
      setPagination(result.pagination);
      setLoadError("");
    } catch (err) {
      setLoadError(errorMessage(err, "Failed to load complaints"));
    } finally {
      setLoading(false);
    }
  }, [appliedFilters, paginationModel, sortModel]);

  const loadKpis = useCallback(async () => {
    if (!can(permissions, PERMISSION.REPORT_VIEW)) return;
    try {
      const params = {};
      if (appliedFilters.from) params.from = appliedFilters.from;
      if (appliedFilters.to) params.to = appliedFilters.to;
      const data = await fetchReport("dashboard", params);
      setKpis(data?.kpis || null);
    } catch {
      // KPIs are a nicety; the grid below is the actual job of this screen.
      setKpis(null);
    }
  }, [appliedFilters.from, appliedFilters.to, permissions]);

  useEffect(() => {
    if (meta) loadComplaints();
  }, [meta, loadComplaints]);

  useEffect(() => {
    if (meta) loadKpis();
  }, [meta, loadKpis]);

  const handleSaved = (message) => {
    notify(message);
    loadComplaints();
    loadKpis();
  };

  /** Switching a quick view resets the status/SLA/assignee presets it owns. */
  const handleViewChange = (key) => {
    const preset = QUICK_VIEWS.find((v) => v.key === key);
    setView(key);
    setFilters((prev) => ({
      ...prev,
      status: "",
      slaState: "",
      assignedTo: "",
      ...preset.filters,
    }));
  };

  const clearFilters = () => {
    setView("all");
    setFilters(EMPTY_FILTERS);
    setAppliedFilters(EMPTY_FILTERS);
  };

  // Only the filters the user set by hand — a quick view is not "a filter" to
  // be cleared, it is the selected tab.
  const manualFilterCount = useMemo(
    () =>
      ["search", "priority", "category", "source", "from", "to"].filter((key) => appliedFilters[key])
        .length,
    [appliedFilters]
  );

  const handleExport = () => {
    if (!rows.length) {
      notify("Nothing to export on this page", "warning");
      return;
    }

    const sheet = rows.map((row) => ({
      "Ticket ID": row.ticketId,
      Status: row.status,
      Priority: row.priority,
      Category: row.category,
      "Sub-category": row.subCategory || "",
      Source: row.source,
      Customer: row.customer?.name || "",
      Mobile: row.customer?.mobile || "",
      "Order ID": row.order?.orderId || "",
      Restaurant: row.order?.restaurantName || "",
      "Order Amount": row.order?.orderAmount || 0,
      "Assigned To": row.assignedTo?.name || "Unassigned",
      Department: row.department || "",
      "SLA State": row.slaStatus?.state || "",
      "SLA Due": row.sla?.resolutionDueAt ? formatDateTime(row.sla.resolutionDueAt) : "",
      "Resolution Type": row.resolution?.type || "",
      "Resolution Amount": row.resolution?.amount || 0,
      "Refund Status": row.resolution?.refundStatus || "",
      CSAT: row.csat?.rating ?? "",
      "Reopen Count": row.reopenCount || 0,
      Created: formatDateTime(row.createdAt),
    }));

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(sheet), "Complaints");
    XLSX.writeFile(workbook, `complaints-${new Date().toISOString().slice(0, 10)}.xlsx`);
    notify(`Exported ${rows.length} complaint(s)`);
  };

  const columns = useMemo(
    () => [
      {
        field: "ticketId",
        headerName: "Ticket",
        width: 215,
        renderCell: (params) => (
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
            <PersonAvatar name={params.row.customer?.name || params.row.ticketId} size={34} />
            <TwoLine
              primary={params.value}
              primarySx={{ fontWeight: 700, color: T.brand }}
              secondary={`${formatRelative(params.row.createdAt)} · ${params.row.source}`}
            />
          </Stack>
        ),
      },
      {
        field: "customer",
        headerName: "Customer",
        width: 175,
        sortable: false,
        renderCell: (params) => (
          <TwoLine
            primary={params.row.customer?.name}
            primarySx={{ fontWeight: 500 }}
            secondary={params.row.customer?.mobile || params.row.customer?.email}
          />
        ),
      },
      {
        field: "category",
        headerName: "Category",
        width: 195,
        renderCell: (params) => (
          <TwoLine
            primary={params.value}
            primarySx={{ fontWeight: 500 }}
            secondary={params.row.subCategory}
          />
        ),
      },
      {
        field: "status",
        headerName: "Status",
        width: 135,
        renderCell: (params) => (
          <Stack direction="row" spacing={0.75} alignItems="center">
            <StatusChip status={params.value} />
            {params.row.reopenCount > 0 && (
              <Tooltip title={`Reopened ${params.row.reopenCount} time(s)`}>
                <Box
                  sx={{
                    fontSize: "0.65rem",
                    fontWeight: 700,
                    color: "#B91C1C",
                    // bgcolor: "#FEE2E2",
                    // borderRadius: "999px",
                    px: 0.6,
                  }}
                >
                  ×{params.row.reopenCount}
                </Box>
              </Tooltip>
            )}
          </Stack>
        ),
      },
      {
        field: "priority",
        headerName: "Priority",
        width: 110,
        renderCell: (params) => <PriorityChip priority={params.value} />,
      },
      {
        field: "slaStatus",
        headerName: "SLA",
        width: 130,
        sortable: false,
        renderCell: (params) => <SlaChip slaStatus={params.value} />,
      },
      {
        field: "assignedTo",
        headerName: "Assigned To",
        width: 165,
        sortable: false,
        renderCell: (params) =>
          params.row.assignedTo?.name ? (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
              <PersonAvatar name={params.row.assignedTo.name} size={26} />
              <Typography sx={{ fontSize: TYPE.body, color: T.ink }} noWrap>
                {params.row.assignedTo.name}
              </Typography>
            </Stack>
          ) : (
            <Chip
              label="Unassigned"
              size="small"
              sx={{
                height: 24,
                fontSize: TYPE.micro,
                fontWeight: 500,
                bgcolor: T.surfaceMuted,
                color: T.inkMuted,
                border: `1px solid ${T.border}`,
                borderRadius: "999px",
              }}
            />
          ),
      },
      {
        field: "order",
        headerName: "Order",
        width: 165,
        sortable: false,
        renderCell: (params) => (
          <TwoLine
            primary={params.row.order?.orderId}
            secondary={params.row.order?.restaurantName}
          />
        ),
      },
      {
        field: "resolution",
        headerName: "Payout",
        width: 115,
        sortable: false,
        renderCell: (params) =>
          params.row.resolution?.amount > 0 ? (
            <Typography
              sx={{ fontSize: TYPE.body, fontWeight: 600, color: T.ink, fontVariantNumeric: "tabular-nums" }}
            >
              {formatCurrency(params.row.resolution.amount)}
            </Typography>
          ) : (
            <Typography sx={{ fontSize: TYPE.body, color: T.inkMuted }}>—</Typography>
          ),
      },
    ],
    []
  );

  const headerActions = (
    <>
      {can(permissions, PERMISSION.REPORT_VIEW) && (
        <Button
          variant="outlined"
          size="small"
          startIcon={<BarChart3 size={16} />}
          onClick={() => navigate("/complaints/reports")}
          sx={ghostButtonSx}
        >
          Reports
        </Button>
      )}
      {(can(permissions, PERMISSION.CATEGORY_MANAGE) ||
        can(permissions, PERMISSION.ROLE_MANAGE) ||
        can(permissions, PERMISSION.SETTINGS_MANAGE)) && (
        <Button
          variant="outlined"
          size="small"
          startIcon={<Settings2 size={16} />}
          onClick={() => navigate("/complaints/settings")}
          sx={ghostButtonSx}
        >
          Settings
        </Button>
      )}
      <Button
        variant="outlined"
        size="small"
        startIcon={<Download size={16} />}
        onClick={handleExport}
        sx={ghostButtonSx}
      >
        Export
      </Button>
      {can(permissions, PERMISSION.CREATE) && (
        <Button
          variant="contained"
          size="small"
          startIcon={<Plus size={16} />}
          onClick={() => setFormOpen(true)}
          sx={primaryButtonSx}
        >
          Register Complaint
        </Button>
      )}
    </>
  );

  return (
    <>
      <PageShell>
            <PageHeader
              icon={MessageSquareWarning}
              title="Complaint Management"
              subtitle={
                meta?.me
                  ? `Register, track and resolve complaints against SLA — signed in as ${meta.me.roleLabel}`
                  : "Register, track and resolve customer complaints against SLA"
              }
              actions={headerActions}
            />

            {loadError && (
              <Alert severity="error" sx={{ mb: 2, borderRadius: T.radiusControl }}>
                {loadError}
              </Alert>
            )}

            {/* ---- KPI strip ---- */}
            {!meta && !loadError ? (
              <TileSkeleton count={6} />
            ) : (
              kpis && (
                <StatRow>
                  <StatTile
                    label="Total"
                    value={formatCompact(kpis.total)}
                    sub={kpis.createdToday ? `${kpis.createdToday} today` : "none today"}
                    icon={Ticket}
                    hint="Complaints in the selected period"
                  />
                  <StatTile
                    label="Open"
                    value={formatCompact(kpis.open)}
                    sub={`${kpis.openUnassigned ?? 0} unassigned`}
                    icon={Inbox}
                    onClick={() => handleViewChange("open")}
                    hint="Not yet resolved or closed — click to filter"
                  />
                  <StatTile
                    label="Overdue"
                    value={formatCompact(kpis.overdue)}
                    sub={kpis.overdue ? "needs attention" : "all on time"}
                    icon={AlertTriangle}
                    tone={kpis.overdue ? "critical" : "good"}
                    onClick={() => handleViewChange("overdue")}
                    hint="Open and past the resolution SLA — click to filter"
                  />
                  <StatTile
                    label="SLA Met"
                    value={`${kpis.slaCompliancePct}%`}
                    sub={`${kpis.slaBreaches} breached`}
                    icon={CheckCircle2}
                    tone={kpis.slaCompliancePct >= 90 ? "good" : kpis.slaCompliancePct >= 75 ? "warning" : "critical"}
                    hint="Of complaints already resolved, the share that met their SLA"
                  />
                  <StatTile
                    label="Avg Resolution"
                    value={kpis.avgResolutionMins ? formatDuration(kpis.avgResolutionMins) : "—"}
                    sub={
                      kpis.avgFirstResponseMins
                        ? `${formatDuration(kpis.avgFirstResponseMins)} first reply`
                        : undefined
                    }
                    icon={Clock3}
                    hint="Average time from registration to resolution"
                  />
                  <StatTile
                    label="Avg CSAT"
                    value={kpis.avgCsat ? `${kpis.avgCsat} / 5` : "—"}
                    sub={kpis.reopenRatePct ? `${kpis.reopenRatePct}% reopened` : undefined}
                    icon={Star}
                    tone={kpis.avgCsat >= 4 ? "good" : kpis.avgCsat ? "warning" : "neutral"}
                    hint="Average post-resolution rating out of 5"
                  />
                </StatRow>
              )
            )}

            {/* ---- Toolbar ---- */}
            <GlassCard sx={{ mb: 2, mt:2, overflow: "visible"  }}>
              <Box sx={{ px: 2.5, borderBottom: `1px solid ${T.border}` }}>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  {/* Underline tabs, sitting on the card's own bottom rule. */}
                  <Tabs
                    value={view}
                    onChange={(_, value) => handleViewChange(value)}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                      minHeight: 46,
                      flexGrow: 1,
                      "& .MuiTabs-flexContainer": { gap: 0.5 },
                      "& .MuiTab-root": {
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: TYPE.small,
                        minHeight: 46,
                        py: 0,
                        px: 1.5,
                        color: T.inkSecondary,
                        transition: "color .15s ease",
                        "&:hover": { color: T.ink },
                      },
                      "& .Mui-selected": { color: `${T.brand} !important` },
                      "& .MuiTabs-indicator": {
                        bgcolor: T.brand,
                        height: 2,
                        borderRadius: "2px 2px 0 0",
                      },
                    }}
                  >
                    {QUICK_VIEWS.map((quickView) => (
                      <Tab key={quickView.key} value={quickView.key} label={quickView.label} />
                    ))}
                  </Tabs>

                  <Tooltip title="Refresh">
                    <IconButton size="small" onClick={loadComplaints}>
                      <RotateCw size={16} color={T.inkSecondary} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="More filters">
                    <IconButton size="small" onClick={() => setShowFilters((prev) => !prev)}>
                      <Badge
                        badgeContent={manualFilterCount}
                        color="primary"
                        sx={{ "& .MuiBadge-badge": { fontSize: "0.6rem", height: 15, minWidth: 15 } }}
                      >
                        <SlidersHorizontal size={16} color={showFilters ? T.brand : T.inkSecondary} />
                      </Badge>
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Box>

              {/* Search is always visible; the rest folds away. */}
              <Box sx={{ p: 2.5 }}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Search ticket, customer, mobile or order ID"
                    value={filters.search}
                    onChange={(e) => setFilters((p) => ({ ...p, search: e.target.value }))}
                    InputProps={{
                      startAdornment: <Search size={17} color={T.inkMuted} style={{ marginRight: 10 }} />,
                      endAdornment: filters.search ? (
                        <IconButton size="small" onClick={() => setFilters((p) => ({ ...p, search: "" }))}>
                          <X size={15} />
                        </IconButton>
                      ) : null,
                      sx: {
                        borderRadius: T.radiusControl,
                        bgcolor: T.surface,
                        fontSize: TYPE.body,
                        "& input::placeholder": { color: T.inkMuted, opacity: 1 },
                      },
                    }}
                    sx={{
                      "& fieldset": { borderColor: T.border },
                      "&:hover fieldset": { borderColor: T.borderStrong },
                    }}
                  />
                  <Typography
                    sx={{ fontSize: TYPE.small, color: T.inkSecondary, whiteSpace: "nowrap" }}
                  >
                    <Box component="strong" sx={{ color: T.ink, fontSize: TYPE.cardTitle }}>
                      {pagination.total ?? 0}
                    </Box>{" "}
                    complaint{(pagination.total ?? 0) === 1 ? "" : "s"}
                  </Typography>
                </Stack>

                <Collapse in={showFilters} timeout="auto" unmountOnExit>
                  <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                    <Grid item xs={6} md={2}>
                      <TextField
                        select fullWidth size="small" label="Priority"
                        value={filters.priority}
                        onChange={(e) => setFilters((p) => ({ ...p, priority: e.target.value }))}
                      >
                        <MenuItem value="">All</MenuItem>
                        {(meta?.priorities || []).map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                      </TextField>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <TextField
                        select fullWidth size="small" label="Category"
                        value={filters.category}
                        onChange={(e) => setFilters((p) => ({ ...p, category: e.target.value }))}
                      >
                        <MenuItem value="">All</MenuItem>
                        {(meta?.categories || []).map((c) => (
                          <MenuItem key={c._id} value={c.name}>{c.name}</MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={6} md={2}>
                      <TextField
                        select fullWidth size="small" label="Source"
                        value={filters.source}
                        onChange={(e) => setFilters((p) => ({ ...p, source: e.target.value }))}
                      >
                        <MenuItem value="">All</MenuItem>
                        {(meta?.sources || []).map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                      </TextField>
                    </Grid>
                    <Grid item xs={6} md={2}>
                      <TextField
                        fullWidth size="small" label="From" type="date"
                        InputLabelProps={{ shrink: true }}
                        value={filters.from}
                        onChange={(e) => setFilters((p) => ({ ...p, from: e.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={6} md={2}>
                      <TextField
                        fullWidth size="small" label="To" type="date"
                        InputLabelProps={{ shrink: true }}
                        value={filters.to}
                        onChange={(e) => setFilters((p) => ({ ...p, to: e.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={6} md={1}>
                      <Button
                        fullWidth size="small" onClick={clearFilters}
                        sx={{ textTransform: "none", color: T.inkSecondary, height: 40 }}
                      >
                        Clear
                      </Button>
                    </Grid>
                  </Grid>
                </Collapse>
              </Box>
            </GlassCard>

            {/* ---- Grid ---- */}
            <GlassCard sx={{ overflow: "hidden" }}>
              {!meta && !loadError ? (
                <ListSkeleton rows={8} height={52} />
              ) : (
                <DataGrid
                  rows={rows}
                  columns={columns}
                  getRowId={(row) => row._id}
                  loading={loading}
                  rowCount={pagination.total ?? 0}
                  paginationMode="server"
                  sortingMode="server"
                  paginationModel={paginationModel}
                  onPaginationModelChange={setPaginationModel}
                  sortModel={sortModel}
                  onSortModelChange={setSortModel}
                  pageSizeOptions={[10, 25, 50, 100]}
                  rowHeight={66}
                  columnHeaderHeight={48}
                  disableRowSelectionOnClick
                  disableColumnMenu
                  onRowClick={(params) => navigate(`/complaints/${params.id}`)}
                  autoHeight
                  // Drives the coloured urgency spine on the left of each row.
                  getRowClassName={(params) => `sla--${params.row.slaStatus?.state || "unknown"}`}
                  slots={{
                    noRowsOverlay: () => (
                      <EmptyState
                        icon={Inbox}
                        title={manualFilterCount || view !== "all" ? "No complaints match this view" : "No complaints yet"}
                        description={
                          manualFilterCount || view !== "all"
                            ? "Try a different tab, or clear the filters to see everything."
                            : "Register the first complaint to start tracking resolution against SLA."
                        }
                        action={
                          manualFilterCount || view !== "all" ? (
                            <Button size="small" onClick={clearFilters} sx={ghostButtonSx} variant="outlined">
                              Clear filters
                            </Button>
                          ) : (
                            can(permissions, PERMISSION.CREATE) && (
                              <Button
                                size="small" variant="contained" startIcon={<Plus size={16} />}
                                onClick={() => setFormOpen(true)} sx={primaryButtonSx}
                              >
                                Register Complaint
                              </Button>
                            )
                          )
                        }
                      />
                    ),
                  }}
                  sx={{
                    border: "none",
                    "--DataGrid-overlayHeight": "340px",
                    "& .MuiDataGrid-row": {
                      cursor: "pointer",
                      transition: "background-color .12s ease",
                      "&:hover": { bgcolor: T.brandSoft },
                    },
                    // Urgency spine: a breached row is visible while scanning,
                    // without needing to read the SLA column.
                    "& .sla--breached .MuiDataGrid-cell:first-of-type": {
                      boxShadow: `inset 3px 0 0 ${SLA_COLORS.breached.dot}`,
                    },
                    "& .sla--at_risk .MuiDataGrid-cell:first-of-type": {
                      boxShadow: `inset 3px 0 0 ${SLA_COLORS.at_risk.dot}`,
                    },
                    "& .sla--on_track .MuiDataGrid-cell:first-of-type": {
                      boxShadow: `inset 3px 0 0 ${SLA_COLORS.on_track.dot}`,
                    },
                    "& .sla--met .MuiDataGrid-cell:first-of-type": {
                      boxShadow: `inset 3px 0 0 ${T.border}`,
                    },
                    "& .MuiDataGrid-columnHeaders": {
                      bgcolor: T.surfaceHeader,
                      borderBottom: `1px solid ${T.border}`,
                    },
                    "& .MuiDataGrid-columnHeaderTitle": { ...labelSx, color: T.inkSecondary },
                    // A separator between every header cell makes a wide grid
                    // easier to scan than one unbroken grey band.
                    "& .MuiDataGrid-columnSeparator": { color: T.border },
                    // Centre every cell's content. Without this, a cell whose
                    // content has no height of its own stretches to fill the
                    // row and its text sits on the top edge, while cells
                    // containing a chip or an avatar sit centred — which is
                    // what made the columns look misaligned.
                    "& .MuiDataGrid-cell": {
                      borderBottom: `1px solid ${T.border}`,
                      px: 2,
                      display: "flex",
                      alignItems: "center",
                    },
                    "& .MuiDataGrid-columnHeader": { px: 2 },
                    "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": { outline: "none" },
                    "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within": {
                      outline: "none",
                    },
                    "& .MuiDataGrid-footerContainer": {
                      borderTop: `1px solid ${T.border}`,
                      bgcolor: T.surfaceHeader,
                      minHeight: 52,
                    },
                    "& .MuiTablePagination-root": { fontSize: TYPE.small, color: T.inkSecondary },
                  }}
                />
              )}
            </GlassCard>
      </PageShell>

      <ComplaintForm
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSaved={handleSaved}
        meta={meta}
      />

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity={toast.severity}
          variant="filled"
          onClose={() => setToast((p) => ({ ...p, open: false }))}
          sx={{ borderRadius: T.radiusControl, fontWeight: 600 }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default Complaints;
